<template>
  <section class="general-section">
    <div class="general-container w-container">
      <h1 class="centered-heading page-title">
        <span class="highlightheading">Support</span> Aafia getting access to Medical Care.
      </h1>
      <div class="general-content-wrapper page-intro">
        <h2 class="sub-title">Consent to Join as Amicus Curiae</h2>
        <h3 class="sub-kicker">In Support of the Right to Adequate Medical Care</h3>

        <div class="general-content rich-text consent-card">
          <p>
            I hereby give consent for my name to be added in support of an
            amicus brief for Aafia Siddiqui's case in the Southern District of
            New York, which raises the fact that, as a critically ill woman, she must be granted proper access to a qualified medical professional.
          </p>
          <p>
            The brief supports the principle that incarcerated individuals
            retain the fundamental right to adequate medical care, including timely access to doctors, necessary treatment, and independent medical evaluation without undue interference from prison authorities, such as strip searches before every doctor visit.
          </p>
          <p>
            By giving consent, I affirm the right of incarcerated individuals
            to receive appropriate medical attention that meets basic humanitarian and legal standards.
          </p>
          <p>
            I consent to have my name, title, and professional affiliation listed
            as a supporter of the amicus brief. I understand that this support
            is personal, and institutional endorsement is not required, although
            it may be included in the permission if specifically given.
          </p>
        </div>

        <div class="consent-form">
          <div class="form-row">
            <label>Full Name</label>
            <input v-model="form.name" :class="{ 'input-error': errors.name }" type="text" placeholder="Enter full name" required />
          </div>

          <div class="form-row">
            <label>Title/Position</label>
            <input v-model="form.title" :class="{ 'input-error': errors.title }" type="text" placeholder="Enter title or position" required />
          </div>

          <div class="form-row">
            <label>Organization (if applicable)</label>
            <input v-model="form.organization" :class="{ 'input-error': errors.organization }" type="text" placeholder="Enter organization" />
          </div>

          <div class="form-row">
            <label>I would like my organization's name included</label>
            <div class="radio-group plain">
              <label class="radio plain"><input type="radio" name="includeOrg" :checked="form.includeOrganizationName === true" @change="form.includeOrganizationName = true" /> Yes</label>
              <label class="radio plain"><input type="radio" name="includeOrg" :checked="form.includeOrganizationName === false" @change="form.includeOrganizationName = false" /> No</label>
            </div>
          </div>

          <div class="form-row two-col signature-row">
            <div class="col">
              <label>Date:</label>
              <div class="date-display" :title="formattedDate">{{ formattedDate }}</div>
            </div>
            <div class="col checkbox-col">
          
              <label class="checkbox-inline confirm-text">
                <input v-model="form.isSigned" type="checkbox" />
                I confirm this is my digital signature
              </label>
            </div>
          </div>

          <p class="note">* Electronic signature is acceptable</p>

          <div class="actions">
            <button
              class="button-primary w-button submit-button"
              :disabled="isSubmitting"
              @click.prevent="submitForm"
            >
              <span v-if="!isSubmitting">Submit</span>
              <span v-else class="btn-loader"></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { useToast } from "vue-toastification";
export default {
  name: "SupportMedicalCare",
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      form: {
        id: 0,
        name: "",
        title: "",
        organization: "",
        includeOrganizationName: false,
        date: new Date().toISOString().slice(0, 10),
        isSigned: false,
      },
      isSubmitting: false,
      errors: {
        name: false,
        title: false,
        organization: false,
        isSigned: false,
      },
      apiToken:
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJhZG1pbkBhYWZpYS5jb20iLCJqdGkiOiJlZWUzYzE4Yi0wOWY1LTRmZWYtOTdiZC03YzM2NTRkYTYxZDYiLCJleHAiOjE3ODg5NDI3MjUsImlzcyI6Im15c2tvb2wuYXBwIiwiYXVkIjoibXlza29vbC5hcHAifQ.0gD28VrRQqPSr3c1nO75oCLuoH_eReiBFXixMRZS-Ro",
    };
  },
  computed: {
    formattedDate() {
      try {
        const source = this.form.date || new Date().toISOString().slice(0, 10);
        const dateObj = new Date(source);
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sept",
          "Oct",
          "Nov",
          "Dec",
        ];
        const day = dateObj.getDate();
        const month = months[dateObj.getMonth()];
        const year = dateObj.getFullYear();
        return `${day} ${month} ${year}`;
      } catch (e) {
        return this.form.date;
      }
    },
  },
  methods: {
    async submitForm() {
      // Validate required fields
      const missing = [];
      this.errors = { name: false, title: false, organization: false, isSigned: false };
      if (!this.form.name?.trim()) { this.errors.name = true; missing.push("Full Name"); }
      if (!this.form.title?.trim()) { this.errors.title = true; missing.push("Title/Position"); }
      if (!this.form.isSigned) { this.errors.isSigned = true; missing.push("Signature Confirmation"); }

      if (missing.length) {
        this.toast.warning(`Please complete: ${missing.join(", ")}.`);
        return;
      }
      try {
        this.isSubmitting = true;
        const payload = {
          ...this.form,
          date: this.form.date ? new Date(this.form.date).toISOString() : new Date().toISOString(),
        };
        const res = await fetch("https://aafia.cloudious.net/api/ConsentForm/Save", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${this.apiToken}`,
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Failed to submit");
        this.toast.success("Thank you! Your consent form has been submitted.");
        this.form = {
          id: 0,
          name: "",
          title: "",
          organization: "",
          includeOrganizationName: false,
          date: new Date().toISOString().slice(0, 10),
          isSigned: false,
        };
        this.errors = { name: false, title: false, organization: false, isSigned: false };
      } catch (e) {
        this.toast.error("There was an error submitting the form. Please try again.");
        // Optionally log e
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.page-title {
  margin-bottom: 10px;
  text-align: center;
}

.page-intro {
  max-width: 860px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
}

.sub-title {
  margin: 0 0 6px 0;
  font-size: 22px;
  font-weight: 700;
}

.sub-kicker {
  margin: 0 0 18px 0;
  font-size: 16px;
  color: #6b7280;
  font-weight: 600;
}

.rich-text p {
  text-align: center;
  color: #111827;
  margin-bottom: 14px;
}

.consent-card {
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #111827;
  text-align: center;
}

.consent-form {
  margin-top: 24px;
  margin-left: auto;
  margin-right: auto;
  max-width: 980px;
  padding: 16px 24px 8px 24px;
  border-radius: 8px;
  background: #f9fbfa;
  border: 1px solid #e8efe9;
}

.form-row {
  display: grid;
  grid-template-columns: 360px 1fr;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}

.form-row.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-row label {
  font-weight: 700;
  margin-bottom: 0;
  text-align: left;
}

.form-row input[type="text"],
.form-row input[type="date"],
.form-row input[type="email"],
.form-row input[type="tel"] {
  padding: 10px 2px;
  border: none;
  border-bottom: 2px solid #1f2937;
  background: transparent;
  border-radius: 0;
  font-size: 16px;
  width: 100%;
  transition: border-color 0.2s ease;
}

.form-row input[type="text"]:focus,
.form-row input[type="date"]:focus,
.form-row input[type="email"]:focus,
.form-row input[type="tel"]:focus {
  outline: none;
  border-bottom-color: #198754;
}

.input-error {
  border-bottom-color: #dc2626 !important; /* red */
}

.radio-group.plain {
  display: inline-flex;
  gap: 18px;
}

.radio.plain {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.note {
  margin-top: 6px;
  font-size: 12px;
  color: #666;
}

.checkbox-inline {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.actions {
  margin-top: 16px;
  text-align: center;
}

.submit-button[disabled] {
  opacity: 0.7;
  cursor: not-allowed;
}

.signature-row {
  padding: 12px;
  border: 2px solid #198754;
  border-radius: 8px;
  background: #eef8f2;
  align-items: center;
}

.signature-row .col {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
}

.date-display {
  font-size: 14px;
  font-weight: 700;
}

.date-field[disabled] {
  background: transparent;
  color: #111827;
  cursor: not-allowed;
  opacity: 1;
  border: none;
  border-bottom: none;
  padding-left: 0;
}

/* Ensure the generic input styles don't re-apply a bottom border */
.form-row .date-field {
  border: none !important;
  border-bottom: none !important;
  box-shadow: none !important;
  outline: none !important;
  -webkit-appearance: none;
  appearance: none;
  width: auto;
  min-width: 140px;
}

.confirm-text {
  font-size: 16px;
  font-weight: 700;
  color: #0f5132;
  display: inline-flex;
  align-items: center;
}

.confirm-text input[type="checkbox"] {
  transform: scale(1.35);
  width: 20px;
  height: 20px;
  margin-right: 10px;
  accent-color: #198754;
}

/* Responsive refinements */
@media (max-width: 768px) {
  .date-display {
    font-size: 16px;
  }
  .confirm-text {
    font-size: 15px;
  }
}

@media (max-width: 600px) {
  /* Stack date and signature for small screens */
  .signature-row {
    grid-template-columns: 1fr !important;
  }
  .signature-row .col {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
  .confirm-text input[type="checkbox"] {
    transform: scale(1.25);
  }
}

.btn-loader {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.6);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .form-row.two-col {
    grid-template-columns: 1fr;
  }
  .form-row {
    grid-template-columns: 1fr;
  }
  .form-row label {
    text-align: left;
    margin-bottom: 6px;
  }

  .sub-title {
    font-size: 20px;
  }

  .sub-kicker {
    font-size: 15px;
  }
}
</style>

